/**
 * The message to put in front of someone when a request fails.
 *
 * Axios's own `err.message` is "Request failed with status code 400", which
 * tells nobody anything. Prefer what the server said, then something plain
 * about the status, and only then the caller's fallback.
 *
 * Pages that call `api.get`/`api.put` straight from axios only have that
 * wording to show; the body's `message` is what service-core sends, and
 * `apiGet`/`apiPost`/`apiPut` in lib/api.ts unwrap it the same way.
 */

const AXIOS_GENERIC = /^Request failed with status code \d+$/

const BY_STATUS: Record<number, string> = {
  400: "That didn't look right to the server. Check the fields and try again.",
  401: 'Your session has expired. Sign in again and retry.',
  403: "You don't have access to do that.",
  404: "That doesn't exist any more — it may have been removed.",
  409: 'Someone changed this while you were editing. Reload and try again.',
  413: 'That is too large to upload.',
  422: "That didn't look right to the server. Check the fields and try again.",
  429: 'Too many requests. Wait a moment and try again.',
}

function fromBody(data: unknown): string | null {
  if (typeof data === 'string') {
    const text = data.trim()
    // An HTML error page is not a message.
    return text && !text.startsWith('<') ? text : null
  }
  if (!data || typeof data !== 'object') return null
  const body = data as Record<string, unknown>
  for (const key of ['message', 'error', 'detail', 'title'] as const) {
    const value = body[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return null
}

/** What the backoffice says when a request needs a module the company's package doesn't have. */
export const MODULE_NOT_IN_PACKAGE = 'Not in your package'

/** The machine code in an error body (`module_not_in_package`, `package_limit`, …), or null. */
export function apiErrorCode(err: unknown): string | null {
  const data = (err as { response?: { data?: unknown } } | null)?.response?.data
  if (!data || typeof data !== 'object') return null
  const code = (data as Record<string, unknown>).code
  return typeof code === 'string' && code.trim() ? code.trim() : null
}

/** A 403 because the company's package doesn't have the module: say "Not in your package", not "no access". */
export function isModuleNotInPackage(err: unknown): boolean {
  return apiErrorCode(err) === 'module_not_in_package'
}

/**
 * What to say for `module_not_in_package`: "Not in your package" for a 403 (the whole action),
 * the server's own sentence for a 400 (one value in an allowed save, e.g. "\"Self pick up\" is
 * not in your package.").
 */
export function moduleNotInPackageMessage(err: unknown): string {
  const response = (err as { response?: { status?: number; data?: unknown } } | null)?.response
  const message = (response?.data as Record<string, unknown> | undefined)?.message
  if (response?.status === 400 && typeof message === 'string' && message.trim()) return message.trim()
  return MODULE_NOT_IN_PACKAGE
}

/**
 * A package limit's message ("Your package allows 3 stores.") is shown as the server
 * wrote it; this is only for a body that came without one.
 */
function packageLimitFallback(data: unknown): string {
  const body = (data ?? {}) as Record<string, unknown>
  const limit = typeof body.limit === 'number' ? body.limit : null
  return limit == null ? 'Your package limit has been reached.' : `Your package allows ${limit}.`
}

export function apiErrorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  // A string thrown as the error is already the message.
  if (typeof err === 'string') return fromBody(err) ?? fallback

  const candidate = err as { response?: { status?: number; data?: unknown }; message?: string } | null

  // Package answers first: a module that's off is not a permission problem.
  const code = apiErrorCode(err)
  if (code === 'module_not_in_package') return moduleNotInPackageMessage(err)

  const fromServer = fromBody(candidate?.response?.data)
  if (fromServer) return fromServer
  if (code === 'package_limit') return packageLimitFallback(candidate?.response?.data)

  const status = candidate?.response?.status
  if (typeof status === 'number') {
    if (BY_STATUS[status]) return BY_STATUS[status]
    if (status >= 500) return 'The server had a problem. Try again in a moment.'
  }

  const message = typeof candidate?.message === 'string' ? candidate.message.trim() : ''
  if (message === 'Network Error') return "Couldn't reach the server. Check your connection and try again."
  if (message && !AXIOS_GENERIC.test(message)) return message

  return fallback
}
